import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Input,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { get } from '../../api/client';

const { Text } = Typography;

/**
 * 一行验证记录（`GET /identity/records` 的 data.items）。
 *
 * 键名是后端 ResponseInterceptor 深度 snake→camel 后的形态。`idCard` 与 `phone` 都是掩码：
 * 身份证号库里本来就只落掩码，手机号库里是原文、在出网前遮的（10-13 裁定：姓名留原文、手机遮）。
 */
interface VerificationRow {
  id: string;
  fullName: string;
  idCard: string | null;
  phone: string;
  provider: string | null;
  success: boolean;
  message: string;
  providerResponse: unknown;
  verificationTime: string;
}

interface RecordsResult {
  items: VerificationRow[];
  total: number;
  page: number;
  pageSize: number;
}

interface StatsResult {
  total: number;
  success: number;
  failure: number;
  successRate: number;
}

const CHANNEL_LABEL: Record<string, string> = {
  aliyun: '阿里云直连',
  aliyunmarket: '阿里云市场',
  newnorwell: 'newnorwell',
  test: 'test（本地调试）',
  local: '本地验证',
};

const stamp = (value: string | null | undefined) =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '—';

export default function IdentityVerificationsPage() {
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [idCard, setIdCard] = useState('');
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [applied, setApplied] = useState<Record<string, string | undefined>>({});
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [detail, setDetail] = useState<VerificationRow | null>(null);

  const startDate = range?.[0] ? range[0].format('YYYY-MM-DD') : undefined;
  const endDate = range?.[1] ? range[1].format('YYYY-MM-DD') : undefined;

  const filters = {
    fullName: applied.fullName,
    phone: applied.phone,
    idCard: applied.idCard,
    startDate: applied.startDate,
    endDate: applied.endDate,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['identity-records', filters, page],
    queryFn: () => get<RecordsResult>('/identity/records', { ...filters, page, pageSize }),
  });

  const { data: stats } = useQuery({
    queryKey: ['identity-stats', filters.startDate, filters.endDate],
    queryFn: () =>
      get<StatsResult>('/identity/stats', { startDate: filters.startDate, endDate: filters.endDate }),
  });

  const search = () => {
    setApplied({
      fullName: fullName.trim() || undefined,
      phone: phone.trim() || undefined,
      // 整号哈希等值命中：库里的 id_card 只有掩码，填不全就必然查不到，这里原样交后端
      idCard: idCard.trim() || undefined,
      startDate,
      endDate,
    });
    setPage(1);
  };

  const columns: TableProps<VerificationRow>['columns'] = [
    { title: '验证时间', dataIndex: 'verificationTime', width: 170, render: stamp },
    { title: '姓名', dataIndex: 'fullName', width: 110 },
    { title: '手机号', dataIndex: 'phone', width: 130 },
    { title: '身份证号', key: 'idCard', width: 170, render: (_, r) => r.idCard || '—' },
    {
      title: '渠道',
      dataIndex: 'provider',
      width: 130,
      render: (v: string | null) => (v ? (CHANNEL_LABEL[v] ?? v) : <Text type="secondary">未记录</Text>),
    },
    {
      title: '结果',
      dataIndex: 'success',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">通过</Tag> : <Tag color="red">未通过</Tag>),
    },
    { title: '说明', dataIndex: 'message', ellipsis: true },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, r) => (
        <Button size="small" onClick={() => setDetail(r)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <Card
      title="实名核验记录"
      extra={
        <Space wrap>
          <Input
            allowClear
            placeholder="姓名"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            onPressEnter={search}
            style={{ width: 130 }}
            maxLength={50}
          />
          <Input
            allowClear
            placeholder="手机号（尾号可）"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onPressEnter={search}
            style={{ width: 160 }}
            maxLength={20}
          />
          <Input
            allowClear
            placeholder="身份证号（整 18 位）"
            value={idCard}
            onChange={(e) => setIdCard(e.target.value)}
            onPressEnter={search}
            style={{ width: 200 }}
            maxLength={18}
          />
          <DatePicker.RangePicker
            value={range as [Dayjs, Dayjs] | null}
            onChange={(v) => setRange(v)}
            allowClear
            placeholder={['开始日期', '结束日期']}
          />
          <Button icon={<SearchOutlined />} onClick={search}>
            查询
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['identity-records'] })}
          />
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="这张表记的是「谁在什么时候、拿哪条渠道验过一次三要素」"
        description="注册流程每次三要素核验都会在这里落一行，成功与失败都落——排查「这个人为什么注册不上」先看这里。身份证号库里只存前 6 后 4 的掩码，所以按身份证号查必须填整 18 位（系统按确定性哈希等值命中，填不全必然查不到）；手机号同样是掩码显示，检索按库中原值匹配。"
      />

      {stats && (
        <Space size={32} wrap style={{ marginBottom: 16 }}>
          <Statistic title="验证次数" value={stats.total} />
          <Statistic title="通过" value={stats.success} />
          <Statistic title="未通过" value={stats.failure} />
          <Statistic title="通过率" value={stats.successRate} precision={1} suffix="%" />
          <Text type="secondary" style={{ alignSelf: 'flex-end', paddingBottom: 6 }}>
            {filters.startDate || filters.endDate
              ? `统计区间：${filters.startDate ?? '不限'} 至 ${filters.endDate ?? '不限'}`
              : '统计的是全部历史（改上方日期区间可收窄）'}
          </Text>
        </Space>
      )}

      <Table<VerificationRow>
        rowKey="id"
        loading={isLoading}
        dataSource={data?.items ?? []}
        columns={columns}
        scroll={{ x: 1100 }}
        locale={{
          emptyText:
            '还没有核验记录：有人在注册流程走完三要素核验、或用「服务凭据 → 实名认证」的渠道测试真调后才会出现在这里',
        }}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />

      <Drawer title="核验详情" width={640} open={detail !== null} onClose={() => setDetail(null)}>
        {detail && (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="验证时间">{stamp(detail.verificationTime)}</Descriptions.Item>
            <Descriptions.Item label="姓名">{detail.fullName}</Descriptions.Item>
            <Descriptions.Item label="手机号">{detail.phone}</Descriptions.Item>
            {detail.idCard && <Descriptions.Item label="身份证号">{detail.idCard}</Descriptions.Item>}
            {detail.provider && (
              <Descriptions.Item label="渠道">
                {CHANNEL_LABEL[detail.provider] ?? detail.provider}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="结果">
              {detail.success ? <Tag color="green">通过</Tag> : <Tag color="red">未通过</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="说明">{detail.message}</Descriptions.Item>
            <Descriptions.Item label="记录 ID">{detail.id}</Descriptions.Item>
            {/* 没返回体就整行不渲染：本地验证与异常兜底那两类记录本来就没有服务商原话 */}
            {detail.providerResponse !== null && detail.providerResponse !== undefined && (
              <Descriptions.Item label="服务商返回">
                <Typography.Paragraph
                  copyable
                  style={{ marginBottom: 0, fontSize: 12.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                >
                  {JSON.stringify(detail.providerResponse, null, 2)}
                </Typography.Paragraph>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>
    </Card>
  );
}
